import { useMemo, useRef } from 'react';
import type { ColDef } from 'ag-grid-community';
import { AgGridTable, type AgGridTableProps } from '@shared/components/ui/AgGridTable';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { mapOrdersToModalList, ProductsWithoutStockModel } from '../../models/ProductsWithoutStock';
import { OrdersWithProductModal, OrdersWithProductModalRef } from '../modals/OrdersWithProductModal';

type SortingConfig = NonNullable<AgGridTableProps['sorting']>;

interface TableProps {
  productsWithoutStock: ProductsWithoutStockModel[];
  isLoading?: boolean;
  className?: string;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  sorting?: SortingConfig | null;
  onSortingChange?: (sorting: SortingConfig | null) => void;
}

export const ProductsWithoutStockAgGridTable = ({
  productsWithoutStock,
  isLoading = false,
  className,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  sorting,
  onSortingChange,
}: TableProps) => {
  const ordersModalRef = useRef<OrdersWithProductModalRef>(null);
  const handleOrderClick = (product: ProductsWithoutStockModel) => {
    const orderData = {
      shop: {
        name: product.shop_name,
      },
      orders: mapOrdersToModalList(product.orders),
    };

    ordersModalRef.current?.openModal(orderData);
  };
  const { allColumnDefs } = useAgGridColumns({ onOrderClick: handleOrderClick });
  const columnDefs = useMemo<ColDef<ProductsWithoutStockModel>[]>(() => allColumnDefs, [allColumnDefs]);

  return (
    <div className="w-full">
      <AgGridTable
        data={productsWithoutStock}
        columnDefs={columnDefs}
        className={`rounded-lg shadow-sm ${className ?? ''}`.trim()}
        height={
          productsWithoutStock.length <= 10
            ? `${Math.max(productsWithoutStock.length * 70 + 120, 260)}px`
            : 'calc(100vh - 150px)'
        }
        rowHeight={70}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isLoading}
        sorting={sorting}
        onSortingChange={onSortingChange}
        suppressCellFocus={true}
      />

      <OrdersWithProductModal ref={ordersModalRef} />
    </div>
  );
};
